import { Component, EventEmitter, OnInit, Output, Input } from '@angular/core';
import { Yii } from "libraries/interfaces/Yii";
import { FormControl, FormGroup } from "@angular/forms";
import { IFieldSpecs } from "libraries/module/formbuilder/fields/interfaces/field-specs";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import {
    ETicketSearchParts,
} from "app_sasha/shared/services/search-form-helper/search-form-helper.service";
import {
    TicketSearchFormMapperService
} from "app_sasha/pages/ticketing-report/components/ticketing-search-form/ticketing-search-form-mapper.service";
import { RequestForTicketingReportSearch } from "@afidium/ts-facade";
import { IOptionsCardForm } from "libraries/module/formbuilder/forms/card-form/card-form.component";
import {
    IDescriptionLabels
} from "libraries/module/formbuilder/fields/form-field/components/field-description/field-description.component";

export class Wording {
    public title: string = Yii.t('wui-sasha-js', "Journal d'émissions - Recherche");
    public error: string = Yii.t('wui-sasha-js', "Merci de vérifier le formulaire");
    public reset: string = Yii.t('wui-sasha-js', "Nouvelle recherche");
    public search: string = Yii.t('wui-sasha-js', "Rechercher");
    public modifySearch: string = Yii.t('wui-sasha-js', "Modifier la recherche");
    public [ETicketSearchParts.freeSearch]: string = Yii.t('wui-sasha-js', "Recherche sur le journal d'émissions");
    public noRequest: string = Yii.t('wui-sasha-js', "Aucun critère de recherche");
    public oneCrit: string = Yii.t('wui-sasha-js', "1 critère de recherche");
    public moreCrit: string = Yii.t('wui-sasha-js', "%placeholder% critères de recherche");
    public description: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js', 'Champ de recherche global.'),
        detailIntroduction: Yii.t('wui-sasha-js', 'Ce champ cherche sur les informations suivantes :'),
        detailList: [
            Yii.t('wui-sasha-js', 'Numéros de billet ;'),
            Yii.t('wui-sasha-js', 'Auteur de l\'action ;'),
            Yii.t('wui-sasha-js', 'Références de dossier ;'),
            Yii.t('wui-sasha-js', 'Noms de voyageur, agent, agence ;'),
            Yii.t('wui-sasha-js', 'Codes d\'agence, d\'agent ;'),
            Yii.t('wui-sasha-js', 'Codes de compagnies aériennes.'),
        ],
        syntaxHelp: [
            Yii.t('wui-sasha-js', 'NOT ou un tiret au début d\'un mot pour rechercher des valeurs qui ne contiennent pas ce mot ;'),
            Yii.t('wui-sasha-js', '* pour rechercher un texte partiel ;'),
            Yii.t('wui-sasha-js', 'AND pour combiner des expressions.'),
        ]
    };
}

@Component({
    selector: 'ticketing-search-form',
    template: `
        <card-form [options]="cardOptions" (onSubmit)="launchSearch()">
            <div class="card-header-content">
                <h1>{{wording.title }}</h1>
                <div class="btn-right">
                    <btn type="actionHeaderLink" (onClick)="reset()" [value]="wording.reset"></btn>
                    <btn type="actionDo" (onClick)="toggleContent(!isContentVisible)"
                         [value]="wording.modifySearch"></btn>
                </div>
            </div>
            <div class="card-body-content">
                <div *ngIf="!isContentVisible">
                    <ng-container *ngIf="!searchRequest">{{wording.noRequest}}</ng-container>
                    <ng-container *ngIf="searchRequest">
                        <ng-container [ngPlural]="(searchRequest | ticketCritCount)">
                            <ng-template ngPluralCase="=0">{{wording.noRequest}}</ng-template>
                            <ng-template ngPluralCase="=1">{{wording.oneCrit}}</ng-template>
                            <ng-template
                                    ngPluralCase="other">{{wording.moreCrit | completeLabel:(searchRequest | ticketCritCountString)}}</ng-template>
                        </ng-container>
                    </ng-container>
                </div>
                <ng-container *ngIf="isContentVisible">
                    <div class="col-to-row-s-mb">
                        <div class="flex-1">
                            <chips [fieldConfig]="freeSearchConfig"></chips>
                        </div>
                    </div>
                    <div class="col-to-row-s-mb sep-top">
                        <div class="flex-1 sep-right">
                            <ticketing-action-fieldset [myFormGroup]="formGroup.controls[parts.action]"
                                                       [apiRequestAction]="searchRequest?.action"></ticketing-action-fieldset>
                            <div class="sep-top"></div>
                            <ticketing-booking-fieldset [apiRequestBooking]="searchRequest?.booking"
                                                        [myFormGroup]="formGroup.controls[parts.booking]"></ticketing-booking-fieldset>
                        </div>
                        <div class="flex-1">
                            <ticketing-ticket-fieldset [apiRequestTicket]="searchRequest?.ticket"
                                                       [myFormGroup]="formGroup.controls[parts.ticket]"></ticketing-ticket-fieldset>
                        </div>
                    </div>
                </ng-container>
            </div>
        </card-form>
    `,
    styles: [`
        .card-body-content {
            padding: .75rem;
        }

        .card-body-content notification {
            margin-bottom: .75rem;
        }

        .btn-right {
            margin-right: .75rem;
        }
    `]
})
export class TicketingSearchFormComponent implements OnInit {

    @Input()
    public isContentVisible: boolean;

    @Input()
    public searchRequest: RequestForTicketingReportSearch;

    @Output()
    public readonly isContentVisibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Output()
    public readonly onSearch: EventEmitter<RequestForTicketingReportSearch> = new EventEmitter<RequestForTicketingReportSearch>();

    @Output()
    public readonly onReset: EventEmitter<void> = new EventEmitter<void>();

    public readonly parts: typeof ETicketSearchParts = ETicketSearchParts;

    public readonly wording: Wording = new Wording();

    public formGroup: FormGroup;

    public freeSearchConfig: IFieldSpecs;
    public cardOptions: IOptionsCardForm = {
        formGroup: null,
        labels: {
            error: this.wording.error,
            submit: this.wording.search,
        },
    };

    constructor(
        private readonly ticketSearchFormMapper: TicketSearchFormMapperService,
    ) {
    }

    public ngOnInit(): void {
        this.initFormGroup();
        this.initFieldConfig();
        this.patchForm();
    }

    public toggleContent(state: boolean): void {
        this.isContentVisible = state;
        this.isContentVisibleChange.emit(this.isContentVisible);
    }

    public reset(): void {
        this.formGroup.reset({});
        this.onReset.emit();
    }

    public launchSearch(): void {
        this.onSearch.emit(
            this.ticketSearchFormMapper.mapFormOutputToRequest(this.formGroup.value)
        );
    }

    public patchForm(): void {
        if (!this.searchRequest) {
            this.formGroup.get('freeSearch').reset([]);
        } else {
            this.formGroup.get('freeSearch').setValue(this.searchRequest?.freeSearchArray || []);
        }
    }

    private initFieldConfig(): void {
        this.freeSearchConfig = {
            formGroup: this.formGroup,
            formControlName: ETicketSearchParts.freeSearch,
            label: this.wording[ETicketSearchParts.freeSearch],
            iconDefinition: faSearch,
            description: this.wording.description,
        };
    }

    private initFormGroup(): void {
        this.formGroup = new FormGroup(
            {
                freeSearch: new FormControl([]),
                [ETicketSearchParts.action]: new FormGroup({}),
                [ETicketSearchParts.booking]: new FormGroup({}),
                [ETicketSearchParts.ticket]: new FormGroup({}),
            }
        );
        this.cardOptions.formGroup = this.cardOptions.formGroup = this.formGroup;

    }
}
